/**
 * useCountdownTimer Hook
 * Manages countdown timer for delivery options expiry
 */

import { useState, useEffect, useCallback, useMemo } from "react";

interface CountdownTimerResult {
  /** Minutes remaining */
  minutes: number;
  /** Seconds remaining */
  seconds: number;
  /** Whether the timer has expired */
  isExpired: boolean;
  /** Formatted time string (MM:SS) */
  formattedTime: string;
  /** Total seconds remaining */
  totalSeconds: number;
  /** Reset the timer with a new expiry time */
  // reset: (newExpiresAt: string) => void;
}

/**
 * Custom hook for countdown timer functionality
 * @param expiresAt - ISO 8601 datetime string for expiry
 * @returns Countdown timer state and controls
 */
export const useCountdownTimer = (expiresAt: string): CountdownTimerResult => {
  const [expiryTime, setExpiryTime] = useState<Date>(new Date(expiresAt));
  const [now, setNow] = useState<Date>(new Date());

  // Calculate remaining time
  const { totalSeconds, minutes, seconds, isExpired } = useMemo(() => {
    const diffMs = expiryTime.getTime() - now.getTime();
    const totalSec = Math.max(0, Math.floor(diffMs / 1000));

    return {
      totalSeconds: totalSec,
      minutes: Math.floor(totalSec / 60),
      seconds: totalSec % 60,
      isExpired: totalSec <= 0,
    };
  }, [expiryTime, now]);

  // Format time as MM:SS
  const formattedTime = useMemo(() => {
    const paddedMinutes = String(minutes).padStart(2, "0");
    const paddedSeconds = String(seconds).padStart(2, "0");
    return `${paddedMinutes}:${paddedSeconds}`;
  }, [minutes, seconds]);

  // Update current time every second
  useEffect(() => {
    if (isExpired) return;

    const intervalId = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isExpired]);

  // Reset the timer with a new expiry time
  // const reset = useCallback((newExpiresAt: string) => {
  //   setExpiryTime(new Date(newExpiresAt));
  //   setNow(new Date());
  // }, []);

  // Update expiry time when prop changes
  useEffect(() => {
    setExpiryTime(new Date(expiresAt));
  }, [expiresAt]);

  return {
    minutes,
    seconds,
    isExpired,
    formattedTime,
    totalSeconds,
    // reset,
  };
};

export default useCountdownTimer;
